import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { stripAudioTagsStreaming } from "@/features/utils/audio-tags";
import { usePublicTranslation } from "@/features/utils/i18n";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowRight,
  BellIcon,
  ChatTextIcon,
  Calendar,
  CalendarCheckIcon,
  CrosshairIcon,
  FileTextIcon,
  Globe,
  LinkIcon,
  MagnifyingGlass,
  Phone,
  ShieldCheckIcon,
  User,
  WrenchIcon,
} from "@phosphor-icons/react";
import React from "react";
import { type BookingEvent } from "../../hooks/use-public-booking";
import { type SessionMessage } from "../../types";
import {
  BookingCalendarCard,
  bookedFromCalendars,
  isBookingCalendar,
} from "../booking/BookingCalendar";
import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { Bubble, BubbleRow } from "./Bubble";
import { ChatMessage, parseFormattedText } from "./ChatMessage";
import { TransferSeparator } from "./TransferSeparator";
import { TypingIndicator } from "./TypingIndicator";

// Flexible session type that works for both public and backoffice sessions
type SessionInfo = {
  id: string;
  name?: string;
  agent_id: string;
  previous_sessions?: Array<{
    id: string;
    name?: string;
    agent_id: string;
  }>;
  agents?: Record<string, { name: string }>; // Optional mapping of agent IDs to names
};

// The glyph of each tool we know. Its words are in the `public` namespace
// (`tools.<name>`): the visitor sees these badges too, in their own language.
const toolIcons: Record<string, React.ReactNode> = {
  get_calendar_events: <Calendar size={12} weight="fill" />,
  create_calendar_event: <Calendar size={12} weight="fill" />,
  update_calendar_event: <Calendar size={12} weight="fill" />,
  check_availability: <Calendar size={12} weight="fill" />,
  accept_transfer: <Phone size={12} weight="fill" />,
  hangup: <Phone size={12} weight="fill" />,
  change_language: <Globe size={12} weight="fill" />,
  transfer_call: <Phone size={12} weight="fill" />,
  get_qualification_responses: <FileTextIcon size={12} weight="fill" />,
  submit_qualification: <FileTextIcon size={12} weight="fill" />,
  save_callback_preferences: <User size={12} weight="fill" />,
  update_customer: <User size={12} weight="fill" />,
  call_back_customer: <Phone size={12} weight="fill" />,
  rag_search: <MagnifyingGlass size={12} weight="fill" />,
  rag_read: <MagnifyingGlass size={12} weight="fill" />,
  schedule_reminder: <BellIcon size={12} weight="fill" />,
  schedule_callback: <BellIcon size={12} weight="fill" />,
  cancel_reminder: <BellIcon size={12} weight="fill" />,
  leave_message: <ChatTextIcon size={12} weight="fill" />,
  team_answer_given: <ChatTextIcon size={12} weight="fill" />,
  send_online_booking_link: <LinkIcon size={12} weight="bold" />,
  verify_identity: <ShieldCheckIcon size={12} weight="fill" />,
  confirm_web_sign_in: <ShieldCheckIcon size={12} weight="fill" />,
  set_focus: <CrosshairIcon size={12} weight="fill" />,
  continue_web_task: <Globe size={12} weight="fill" />,
  show_booking_calendar: <CalendarCheckIcon size={12} weight="fill" />,
};

/**
 * A tool the list above does not know — a tenant's own API or MCP tool, or one
 * added on the server before its label here — is still shown in words:
 * `find_availabilities` reads "Find availabilities", never the identifier.
 */
export function humanizeToolName(
  name: string | undefined,
  fallback = "Outil",
): string {
  const words = (name || "").replace(/[_-]+/g, " ").trim();
  if (!words) return fallback;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// One badge per tool, at the place of its first call but carrying the outcome of its
// last one: a tool retried after a failure (missing e-mail, then saved) reads as done.
function dedupeTools<T extends SessionMessage>(tools: T[]): T[] {
  return tools
    .filter(
      (tool, index, arr) =>
        index === arr.findIndex((t) => t.tool?.name === tool.tool?.name),
    )
    .map((first) => {
      const last = [...tools]
        .reverse()
        .find((t) => t.tool?.name === first.tool?.name);
      return last && last !== first && first.tool && last.tool
        ? {
            ...first,
            tool: {
              ...first.tool,
              status: last.tool.status,
              incident: last.tool.incident,
            },
          }
        : first;
    });
}

// Group consecutive tool messages
function groupMessages<T extends SessionMessage>(messages: T[]) {
  const groups: (T | T[])[] = [];
  let currentTools: T[] = [];

  for (const message of messages) {
    if (message.type === "tool") {
      currentTools.push(message);
    } else {
      if (currentTools.length > 0) {
        // Remove duplicates and add the tool group
        groups.push(dedupeTools(currentTools));
        currentTools = [];
      }
      groups.push(message);
    }
  }

  // Add remaining tools if any
  if (currentTools.length > 0) {
    groups.push(dedupeTools(currentTools));
  }

  return groups;
}

// Start timestamp of a group (first message) and end timestamp of a group (last message's
// end, falling back to its start). Used to surface the silence between two consecutive
// messages in debug/sysadmin mode.
//
// An agent message is stored with the timestamp of the moment its turn started computing,
// which is 1-2s before anything is audible; `metadata.timings.spoke_at` records when it
// actually started playing. Measuring from the stored timestamp made every reply look
// instantaneous, so the delay after a caller's turn never even crossed the display
// threshold below.
function groupStart<T extends SessionMessage>(
  group: T | T[],
): number | undefined {
  const first = Array.isArray(group) ? group[0] : group;
  if (!first) return undefined;
  const spokeAt = first.metadata?.timings?.spoke_at;
  const end = first.timestamp_ended ?? first.timestamp;
  if (
    typeof spokeAt === "number" &&
    spokeAt >= first.timestamp &&
    spokeAt <= end
  ) {
    return spokeAt;
  }
  return first.timestamp;
}
function groupEnd<T extends SessionMessage>(
  group: T | T[],
): number | undefined {
  const last = Array.isArray(group) ? group[group.length - 1] : group;
  return last?.timestamp_ended ?? last?.timestamp;
}

// Only surface gaps longer than this (ms) — shorter pauses are just natural turn-taking.
const GAP_DISPLAY_THRESHOLD_MS = 2000;

/**
 * A notification is written once, as the HTML body of an e-mail, and the same text is
 * appended to the conversation. The backend flattens it before storing it (see
 * `Sessions.notify`), but every notification stored before that still carries its tags —
 * and the bubble renders plain text, so the customer read `Bonjour,<br/><br/>…`. Flatten
 * whatever we are handed rather than trusting the row.
 */
function notificationToText(value: string): string {
  return (
    value
      .replace(/<\s*br\s*\/?\s*>/gi, "\n")
      .replace(/<\s*\/\s*(p|div|li|h[1-6])\s*>/gi, "\n")
      // The bubbles parse `**` as bold, which is what the tag meant.
      .replace(/<\s*\/?\s*(b|strong)\s*>/gi, "**")
      .replace(/<[^>]*>/g, "")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
}

interface ChatMessagesProps {
  allMessages: Array<SessionMessage & { sessionId?: string; agentId?: string }>;
  // Live streamed text, rendered as a partial bubble. The public chat deliberately
  // omits it (it only shows the typing indicator via `isTyping`).
  streamingChunk?: string;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  /**
   * The last bubble of the thread. An answer taller than the viewport is scrolled to its
   * *top* rather than to the end of the thread — see the auto-scroll in `use-chat`.
   */
  lastMessageRef?: React.RefObject<HTMLDivElement | null>;
  onPlayMessage?: (timestamp: number) => void;
  playingMessageTimestamp?: number | null;
  isDebugMode?: boolean;
  events?: BookingEvent[];
  onEventClick?: (event: BookingEvent) => void;
  session?: SessionInfo; // Flexible session info
  onGoToLastTransfer?: () => void; // Callback to navigate to next transfer
  // Public-chat personality (backoffice call review omits these to stay neutral):
  showAgentAvatar?: boolean; // Prefix agent bubbles with the agent avatar
  agentName?: string;
  agentLogo?: string | null;
  isTyping?: boolean; // Show the "assistant is typing" bubble
  emptyState?: React.ReactNode; // Custom branded empty state
  // Blank space kept at the top of the thread, for a caller that floats something over
  // it (the voice ring) — the messages then scroll clear of it instead of under it.
  topSpacerClass?: string;
  /** `compact` for the back-office review — see `ChatMessage`. */
  density?: "chat" | "compact";
  /**
   * Show how each tool ended: a real incident in red, a tool that sent the agent back
   * for something (missing information, slot taken) marked "incomplete". The team's
   * view only — the visitor on the widget never sees it.
   */
  showToolOutcome?: boolean;
  /**
   * Show what a HeyUp administrator wrote to the agent (`admin_message`). The
   * back-office's thread only: the customer never sees those, and the server does
   * not send them to the widget either.
   */
  showStaffMessages?: boolean;
  /**
   * The booking calendars the agent opens in the chat (`show_booking_calendar`). Each
   * leaves a card in the thread — the booking made from it, or the way back into it —
   * and the calendar itself opens over the chat (`BookingCalendarPanel`, `onOpen`). The
   * public chat only: the back-office keeps the tool's badge, and reads the booking
   * from the rows it wrote.
   */
  bookingCalendar?: { onOpen: (callId: string) => void };
}

export function ChatMessages({
  allMessages,
  streamingChunk,
  messagesEndRef,
  lastMessageRef,
  onPlayMessage,
  playingMessageTimestamp,
  isDebugMode: isDebugModeProp,
  events,
  onEventClick,
  session,
  onGoToLastTransfer,
  showAgentAvatar,
  agentName,
  agentLogo,
  isTyping,
  emptyState,
  topSpacerClass,
  density = "chat",
  showToolOutcome = false,
  showStaffMessages = false,
  bookingCalendar,
}: ChatMessagesProps) {
  const { t } = usePublicTranslation();
  const compact = density === "compact";
  const messageGroups = groupMessages<
    SessionMessage & { sessionId?: string; agentId?: string }
  >(
    allMessages
      .map((a) => {
        if (a.type === "event" && a.event === "notification") {
          // Notification (SMS / Emails) will be shown in the UI
          return {
            ...a,
            type: "agent",
            value: notificationToText(a.value || a.event_value || ""),
          } as SessionMessage;
        }
        return a;
      })
      // Events are our own bookkeeping — except an administrator's instruction to the
      // agent, which the team's view shows and the visitor's never receives.
      .filter(
        (m) =>
          m.type !== "event" ||
          (showStaffMessages && m.event === "admin_message"),
      ),
  );

  // Booking calendars: each card is drawn under the agent's sentence that introduces it
  // (the message right after its tool, "réservez directement ci-dessous"), or under its
  // badge when the agent said nothing after it.
  const calendarsAfter = new Map<number, SessionMessage[]>();
  const bookedSlots = bookedFromCalendars(allMessages);
  if (bookingCalendar) {
    for (const calendar of allMessages.filter(isBookingCalendar)) {
      // Its badge's group: the last tool group that started at or before it.
      let index = -1;
      messageGroups.forEach((group, i) => {
        if (Array.isArray(group) && group[0].timestamp <= calendar.timestamp) {
          index = i;
        }
      });
      if (index < 0) continue;
      const nextGroup = messageGroups[index + 1];
      const anchor =
        nextGroup && !Array.isArray(nextGroup) && nextGroup.type === "agent"
          ? index + 1
          : index;
      calendarsAfter.set(anchor, [
        ...(calendarsAfter.get(anchor) || []),
        calendar,
      ]);
    }
  }
  const renderCalendars = (groupIndex: number) =>
    bookingCalendar &&
    calendarsAfter.get(groupIndex)?.map((calendar) => (
      <BubbleRow
        key={calendar.tool!.call_id}
        side="agent"
        avatar={
          // A blank of the avatar's size: the card lines up with the bubbles above.
          showAgentAvatar && <div aria-hidden className="size-7 shrink-0" />
        }
      >
        <BookingCalendarCard
          booked={bookedSlots.get(calendar.tool!.call_id)}
          onOpen={() => bookingCalendar.onOpen(calendar.tool!.call_id)}
        />
      </BubbleRow>
    ));

  // Debug (delays, tool inputs and results, model metrics) is the caller's call
  // and never the URL's: `?debug=1` on the public widget used to hand all of it to
  // any visitor. The public chat grants it to a SYSADMIN only (`chat/index.tsx`).
  const isDebugMode = !!isDebugModeProp;

  // Build agent info map for transfer separators
  const agentInfoMap = React.useMemo(() => {
    const map = new Map<string, { agent_id: string; agent_name?: string }>();

    // Helper to get agent name from agents mapping or fallback
    const getAgentName = (agentId: string) => {
      if (session?.agents?.[agentId]) {
        return session.agents[agentId].name;
      }
      return `Agent ${agentId}`;
    };

    // Add info from previous sessions
    if (session?.previous_sessions) {
      session.previous_sessions.forEach((prevSession) => {
        map.set(prevSession.id, {
          agent_id: prevSession.agent_id,
          agent_name: getAgentName(prevSession.agent_id),
        });
      });
    }

    // Add info from current session
    if (session) {
      map.set(session.id, {
        agent_id: session.agent_id,
        agent_name: getAgentName(session.agent_id),
      });
    }

    return map;
  }, [session]);

  return (
    <ScrollArea className="flex-1 px-4">
      <div className={compact ? "space-y-1 mt-3 pb-2" : "space-y-4 mt-4"}>
        {topSpacerClass && <div aria-hidden className={topSpacerClass} />}
        {allMessages.length === 0 && !streamingChunk && !isTyping && (
          <>
            {emptyState ?? (
              <div className="flex items-center justify-center h-full text-center text-muted-foreground">
                <p>{t("chat.empty")}</p>
              </div>
            )}
          </>
        )}

        {messageGroups.map((group, groupIndex) => {
          // Check if we need to show a transfer separator before this message
          let showTransferSeparator = false;
          let fromAgentName: string | undefined;
          let toAgentName: string | undefined;

          // Silence (ms) between the previous message ending and this one starting.
          let gapMs: number | null = null;
          // Compact keeps consecutive turns of one speaker tight and opens a little
          // air where the other one takes over — that is how a transcript is scanned.
          let speakerChanged = false;

          if (groupIndex > 0) {
            const prevGroup = messageGroups[groupIndex - 1];
            const currentGroup = group;

            const prevEnd = groupEnd(prevGroup);
            const currentStart = groupStart(currentGroup);
            if (prevEnd !== undefined && currentStart !== undefined) {
              gapMs = currentStart - prevEnd;
            }
            const speaker = (g: typeof group) =>
              Array.isArray(g) ? "tool" : g.type;
            speakerChanged = speaker(prevGroup) !== speaker(currentGroup);

            // Get agent IDs for comparison
            const prevAgentId = Array.isArray(prevGroup)
              ? prevGroup[0]?.agentId
              : prevGroup?.agentId;
            const currentAgentId = Array.isArray(currentGroup)
              ? currentGroup[0]?.agentId
              : currentGroup?.agentId;

            // Get session IDs for comparison
            const prevSessionId = Array.isArray(prevGroup)
              ? prevGroup[0]?.sessionId
              : prevGroup?.sessionId;
            const currentSessionId = Array.isArray(currentGroup)
              ? currentGroup[0]?.sessionId
              : currentGroup?.sessionId;

            // Show separator if agent or session changed
            if (
              prevAgentId &&
              currentAgentId &&
              (prevAgentId !== currentAgentId ||
                prevSessionId !== currentSessionId)
            ) {
              showTransferSeparator = true;
              const prevInfo = agentInfoMap.get(prevSessionId || "");
              const currentInfo = agentInfoMap.get(currentSessionId || "");
              fromAgentName = prevInfo?.agent_name;
              toAgentName = currentInfo?.agent_name;
            }
          }

          return (
            <React.Fragment key={`group-${groupIndex}`}>
              {compact && speakerChanged && (
                <div aria-hidden className="h-1.5" />
              )}
              {showTransferSeparator && (
                <TransferSeparator
                  fromAgentName={fromAgentName}
                  toAgentName={toAgentName}
                />
              )}

              {/* Sysadmin/debug: surface the silence between two messages when it's long
                  enough to be worth noticing (e.g. slow tool/LLM/endpointing). */}
              {isDebugMode &&
                gapMs !== null &&
                gapMs > GAP_DISPLAY_THRESHOLD_MS && (
                  <div
                    className={cn("flex justify-center", compact && "-my-0.5")}
                  >
                    <span
                      className="text-[10px] font-medium text-warning"
                      title="Délai entre la fin du message précédent et le début de celui-ci"
                    >
                      {(gapMs / 1000).toFixed(1)}s
                    </span>
                  </div>
                )}

              {Array.isArray(group) ? (
                // Tool group — centered horizontally in the thread.
                <div className="flex justify-center">
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {group.map((tool, toolIndex) => {
                      // Exact name first, then a known prefix (`rag_search_faq` is
                      // still a documentation search).
                      const name = tool.tool?.name || "";
                      const known =
                        name in toolIcons
                          ? name
                          : Object.keys(toolIcons).find((a) =>
                              name.startsWith(a),
                            );
                      const translation = known
                        ? { icon: toolIcons[known], label: t(`tools.${known}`) }
                        : {
                            icon: <WrenchIcon size={12} weight="fill" />,
                            label: humanizeToolName(name, t("tools.fallback")),
                          };

                      const failed =
                        showToolOutcome && tool.tool?.status === "error";
                      const isIncident = failed && tool.tool?.incident === true;
                      const toolBadge = (
                        <span
                          key={`${tool.tool?.name}-${toolIndex}`}
                          className={cn(
                            // Wraps like the words it holds: a long label on a
                            // 360px widget used to push the badge past the panel.
                            "text-xs rounded-full flex items-center gap-1 max-w-full [&>svg]:shrink-0",
                            isIncident
                              ? "text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950/60"
                              : "text-muted-foreground bg-muted/50",
                            compact ? "px-2 py-0.5" : "px-2 py-1",
                          )}
                        >
                          {translation.icon}
                          {translation.label}
                          {failed && (
                            <span className="opacity-80">
                              ·{" "}
                              {t(
                                isIncident
                                  ? "tool_outcome.failed"
                                  : "tool_outcome.incomplete",
                              )}
                            </span>
                          )}
                        </span>
                      );

                      // If debug mode is enabled, wrap in tooltip
                      if (isDebugMode) {
                        const debugInfo = {
                          name: tool.tool?.name,
                          call_id: tool.tool?.call_id,
                          status: tool.tool?.status,
                          input: tool.tool?.input,
                          value: tool.value,
                          timestamp: tool.timestamp,
                          timestamp_ended: tool.timestamp_ended,
                        };

                        return (
                          <TooltipProvider
                            key={`${tool.tool?.name}-${toolIndex}`}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {toolBadge}
                              </TooltipTrigger>
                              <TooltipContent className="max-w-md">
                                <div className="text-left space-y-1">
                                  <div>
                                    <strong>Outil :</strong> {debugInfo.name}
                                  </div>
                                  {debugInfo.call_id && (
                                    <div>
                                      <strong>ID d'appel :</strong>{" "}
                                      {debugInfo.call_id}
                                    </div>
                                  )}
                                  {debugInfo.status && (
                                    <div>
                                      <strong>Statut :</strong>{" "}
                                      {debugInfo.status}
                                    </div>
                                  )}
                                  {debugInfo.input && (
                                    <div>
                                      <strong>Entrée :</strong>
                                      <pre className="mt-1 text-xs bg-black/20 p-2 rounded overflow-x-auto">
                                        {JSON.stringify(
                                          debugInfo.input,
                                          null,
                                          2,
                                        )}
                                      </pre>
                                    </div>
                                  )}
                                  {debugInfo.value && (
                                    <div>
                                      <strong>Valeur :</strong>
                                      <pre className="mt-1 text-xs bg-black/20 p-2 rounded overflow-x-auto max-h-32">
                                        {debugInfo.value.substring(0, 500)}
                                        {debugInfo.value.length > 500
                                          ? "..."
                                          : ""}
                                      </pre>
                                    </div>
                                  )}
                                  <div>
                                    <strong>Horodatage :</strong>{" "}
                                    {new Date(
                                      debugInfo.timestamp,
                                    ).toLocaleString("fr-FR")}
                                  </div>
                                  {debugInfo.timestamp_ended && (
                                    <div>
                                      <strong>Durée :</strong>{" "}
                                      {debugInfo.timestamp_ended -
                                        debugInfo.timestamp}{" "}
                                      ms
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      }

                      return toolBadge;
                    })}
                  </div>
                </div>
              ) : null}
              {Array.isArray(group) && renderCalendars(groupIndex)}
              {Array.isArray(group) ? null : (
                // Regular message
                <div
                  ref={
                    groupIndex === messageGroups.length - 1
                      ? lastMessageRef
                      : undefined
                  }
                >
                  <ChatMessage
                    message={group}
                    onPlayMessage={onPlayMessage}
                    playingMessageTimestamp={playingMessageTimestamp}
                    events={events}
                    onEventClick={onEventClick}
                    isDebugMode={isDebugMode}
                    showAgentAvatar={showAgentAvatar}
                    agentName={agentName}
                    agentLogo={agentLogo}
                    density={density}
                  />
                  {calendarsAfter.has(groupIndex) && (
                    <div className="mt-2">{renderCalendars(groupIndex)}</div>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Streaming chunk bubble */}
        {streamingChunk && (
          <BubbleRow
            side="agent"
            avatar={
              showAgentAvatar && (
                <AgentAvatar
                  name={agentName}
                  logo={agentLogo}
                  className="mb-1 size-7"
                />
              )
            }
          >
            {/* The committed agent message's own bubble, so the swap from live stream
                to final message is seamless (no reflow/flash). Bold/italics are parsed
                live instead of only after the stream ends. */}
            <Bubble side="agent">
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {parseFormattedText(
                  stripAudioTagsStreaming(streamingChunk).trimStart(),
                )}
                <span className="inline-block w-1 h-4 ml-1 bg-current animate-pulse" />
              </p>
            </Bubble>
          </BubbleRow>
        )}

        {/* "Assistant is typing" bubble — shown while waiting for the first chunk.
            Always mounted so it can animate out instead of vanishing (see the
            component): removing it outright made the thread jump. */}
        <TypingIndicator
          agentName={agentName}
          agentLogo={agentLogo}
          show={!!isTyping && !streamingChunk}
        />

        {/* Transfer continuation indicator */}
        {onGoToLastTransfer && (
          <div className="flex items-center gap-3 my-6 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" />
            <button
              onClick={onGoToLastTransfer}
              className="flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-full border hover:bg-muted transition-colors cursor-pointer"
            >
              <span className="font-medium">
                {t("chat.continues_elsewhere")}
              </span>
              <ArrowRight size={14} weight="bold" />
            </button>
            <div className="flex-1 h-px bg-border" />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
}
